import { Color, Spacing } from '@persona-id/design-tokens';
import { compact, groupBy, kebabCase, keyBy, last, mapKeys, uniq } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import FieldAttributeItem from 'dashboard/components/FieldAttributeItem';
import InquiryDocuments from 'dashboard/components/InquiryDocuments';
import Permissioned from 'dashboard/components/Permissioned';
import { SimulatedDataSticker } from 'dashboard/components/SimulatedDataSticker/SimulatedDataSticker';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import AsyncSelfie from 'dashboard/components-lib/AsyncSelfie';
import { formatAddressFieldDisplay, formatDate } from 'dashboard/lib/utils/formatters';
import {
  BankStatementDataField,
  Document,
  DocumentGeneric,
  DocumentGovernmentId,
} from 'dashboard/models/document';
import { EnvironmentType } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { Selfie } from 'dashboard/models/selfie';
import { Template } from 'dashboard/models/template';
import { Verification, VerificationGovernmentId } from 'dashboard/models/verification';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import { VERIFICATION_PAGE_DISPLAY } from 'lib/constants/verification';
import { Field, RELATION_FIELD_SCHEMA_TYPES } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { Permission } from 'lib/permissions';
import { VerificationStatus } from 'lib/verification';

const DescriptionContainer = styled('div').attrs(() => ({ role: 'heading', 'aria-level': 2 }))`
  line-height: 2.5;
  font-weight: ${FontWeight.Medium};
  border-bottom: 1px solid ${Color.CoreSlate50};
  grid-column: 1/-1;
`;
const PhotoStickerContainer = styled('div')`
  position: absolute;
  top: ${Spacing[4]};
  left: ${BASE * 2.5}px;
  z-index: 1;
`;
const PhotoWithStickerContainer = styled('div')`
  position: relative;
  display: grid;
  grid-column-gap: ${Spacing[4]};
  grid-template-rows: auto auto;
`;

interface PhotoWithStickerProps {
  imageUrl: string;
  isRedacted: boolean;
  modalLabel: string;
  stickerLabel: string;
  isSelfie?: boolean;
  sourceToken?: string;
}

const PhotoWithSticker = ({
  imageUrl,
  isRedacted,
  modalLabel,
  stickerLabel,
  isSelfie = false,
  sourceToken,
}: PhotoWithStickerProps) => {
  return (
    <PhotoWithStickerContainer>
      <PhotoStickerContainer>
        <Sticker variant='primary'>{stickerLabel}</Sticker>
      </PhotoStickerContainer>
      {isSelfie ? (
        <AsyncSelfie
          value={isRedacted ? [] : [{ url: imageUrl }]}
          width='100%'
          height='270px'
          modalLabel={modalLabel}
          allowModalExpand={false}
          sourceToken={sourceToken}
        />
      ) : (
        <AsyncImage
          value={isRedacted ? [] : [{ url: imageUrl }]}
          width='100%'
          height='270px'
          modalLabel={modalLabel}
          allowModalExpand={true}
        />
      )}
    </PhotoWithStickerContainer>
  );
};

interface InquiryCollectedProps {
  inquiry: Inquiry;
  inquiryTemplateVersion: InquiryShowViewInquiryTemplateVersion | undefined;
  documents: Document[];
  featureFlags: FeatureFlagsMap;
  verifications: Verification[];
  selfies: Selfie[];
  template: InquiryTemplate | Template;
  isRedacted: boolean;
  environmentType: EnvironmentType;
}

const InquiryCollected = ({
  featureFlags,
  inquiry,
  inquiryTemplateVersion,
  documents,
  selfies,
  template,
  isRedacted,
  verifications,
  environmentType,
}: InquiryCollectedProps) => {
  const {
    birthdate,
    'social-security-number': socialSecurityNumber,
    'address-city': addressCity,
    'address-postal-code': addressPostalCode,
    'address-country-code': addressCountryCode,
    'address-street-1': addressStreet1,
    'address-street-2': addressStreet2,
    'address-subdivision': addressSubdivision,
    'name-full': nameFull,
    'name-full-localized': nameFullLocalized,
    'phone-number': phoneNumber,
    'email-address': emailAddress,
  } = inquiry.attributes;

  // COLLECTED PHOTO LOGIC
  // ---------------------
  // COLLECTED GOV ID PHOTO LOGIC
  const documentsByType = groupBy('type', documents);
  const genericDocuments = documentsByType[ApiModel.DocumentGeneric] as DocumentGeneric[];
  const governmentIdDocuments = documentsByType[
    ApiModel.DocumentGovernmentId
  ] as DocumentGovernmentId[];

  const governmentIdVerifications: VerificationGovernmentId[] = verifications.filter(
    (v): v is VerificationGovernmentId => {
      return v.type === ApiModel.VerificationGovernmentId;
    },
  );

  const passedGovIdVerifications = governmentIdVerifications.filter(
    (v) => v.attributes.status === VerificationStatus.Passed,
  );

  const governmentIdVerification = passedGovIdVerifications?.[0] ?? governmentIdVerifications?.[0];
  // Falls back to latest GovId Document Relation field, if available
  const fallbackGovernmentIdDocument = last(governmentIdDocuments);
  // Last processed photo is usually the display photo
  // TODO: Consolidate display photo logic in backend
  const govIdFrontPhoto =
    governmentIdVerification?.attributes['front-photo-url'] ??
    last(fallbackGovernmentIdDocument?.attributes?.['front-photos-processed'])?.url;
  const govIdBackPhoto =
    governmentIdVerification?.attributes['back-photo-url'] ??
    last(fallbackGovernmentIdDocument?.attributes?.['back-photos-processed'])?.url;
  const govIdDisplayPhotos = governmentIdVerification?.attributes['photo-urls'];
  const governmentIdentificationNumbers = compact(
    uniq(
      (passedGovIdVerifications.length > 0
        ? passedGovIdVerifications
        : governmentIdVerifications
      ).map((v: VerificationGovernmentId) => v.attributes['identification-number']),
    ),
  );

  // COLLECTED SELFIE PHOTO LOGIC
  // selfie-photo-url can either be the image from a Verification::Selfie or a
  // cropped portrait image from a Verification::GovernmentId.
  // We only want to show it as collected selfie if a selfie verification exists.
  const inquirySelfiePhotoUrl = verifications.some((v) => v.type === ApiModel.VerificationSelfie)
    ? inquiry.attributes['selfie-photo-url']
    : null;

  const fallbackSelfieToShow = last(selfies);
  let fallbackSelfiePhotoUrl = null;
  switch (fallbackSelfieToShow?.type) {
    case ApiModel.SelfieCenterOnly:
      fallbackSelfiePhotoUrl = fallbackSelfieToShow.attributes['photo-url'];
      break;
    case ApiModel.SelfieProfileAndCenter:
      fallbackSelfiePhotoUrl = fallbackSelfieToShow.attributes['center-photo-url'];
      break;
  }

  const hasGovernmentIdPhotosToShow =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    govIdFrontPhoto != null || govIdBackPhoto != null || govIdDisplayPhotos?.length > 0;
  const hasSelfiePhotoToShow = inquirySelfiePhotoUrl != null || fallbackSelfiePhotoUrl != null;

  // If there are 1 or 2 photos, use 2 columns. Otherwise, use 3 columns.
  const photoLength =
    [govIdFrontPhoto, govIdBackPhoto, hasSelfiePhotoToShow].filter(Boolean).length +
    (govIdDisplayPhotos?.length || 0);
  const photoCols = photoLength > 2 ? 3 : 2;

  let photosHeaderText;
  if (hasGovernmentIdPhotosToShow && hasSelfiePhotoToShow) {
    photosHeaderText = 'Government ID + Selfie';
  } else if (hasGovernmentIdPhotosToShow) {
    photosHeaderText = 'Government ID';
  } else if (hasSelfiePhotoToShow) {
    photosHeaderText = 'Selfie';
  }

  const docViewerPathname = `/dashboard/document-viewer/${
    governmentIdVerification?.relationships?.document?.data?.id ?? fallbackGovernmentIdDocument?.id
  }`;
  const govIdDocument = (
    <>
      {govIdFrontPhoto && (
        <Link
          to={docViewerPathname}
          state={{ internalRedirect: true }}
          // TODO: support a11y attributes in AsyncImage so we can target in specs
          data-test='inquiry-collected__gov-id--front-photo'
        >
          <AsyncImage
            imageUrl={govIdFrontPhoto}
            modalLabel='Front'
            width='initial'
            height='250px'
            allowModalExpand={false}
          />
        </Link>
      )}
      {govIdBackPhoto && (
        <Link
          to={docViewerPathname}
          state={{ internalRedirect: true }}
          // TODO: support a11y attributes in AsyncImage so we can target in specs
          data-test='inquiry-collected__gov-id--back-photo'
        >
          <AsyncImage
            imageUrl={govIdBackPhoto}
            modalLabel='Back'
            width='initial'
            height='250px'
            allowModalExpand={false}
          />
        </Link>
      )}
    </>
  );

  // COLLECTED FIELDS LOGIC
  const fieldSchemas =
    template?.type === ApiModel.Template
      ? template.attributes['v2-field-schemas']
      : inquiryTemplateVersion?.attributes['field-schema'];

  // Snake case because we inflect field keys for `fields` but not for `field-schemas`.
  // Ideally we don't inflect field keys, ever.
  const fieldSchemaMap = useMemo(
    () => keyBy(({ key }) => kebabCase(key), fieldSchemas || []),
    [fieldSchemas],
  );
  const fieldsKebabCased = useMemo(
    () => mapKeys(kebabCase, inquiry.attributes.fields ?? {}),
    [inquiry.attributes.fields],
  );

  const formattedBirthdate =
    birthdate != null ? (
      <div>
        <p>{formatDate(birthdate)}</p>
        <p>
          ({Math.floor(DateTime.local().diff(DateTime.fromISO(birthdate), 'years').years)} years
          old)
        </p>
      </div>
    ) : undefined;

  const address = formatAddressFieldDisplay({
    addressStreet1: addressStreet1,
    addressStreet2: addressStreet2,
    addressCity: addressCity,
    addressSubdivision: addressSubdivision,
    addressPostalCode: addressPostalCode,
    addressCountryCode: addressCountryCode,
  });

  const truncatedSSN = socialSecurityNumber?.substring(socialSecurityNumber?.length - 4);

  const hideEmptyFields = useFeatureFlag('dashboard:hide-empty-inquiry-details-fields');
  const fieldNamesToShow = (fieldSchemas || [])
    .map((fieldSchema) => ({ ...fieldSchema, key: kebabCase(fieldSchema.key) }))
    .filter((fieldSchema) => !hideEmptyFields || fieldsKebabCased[fieldSchema.key]?.value != null)
    .filter((fieldSchema) => !RELATION_FIELD_SCHEMA_TYPES.has(fieldSchema.type))
    .filter(
      (fieldSchema) => !Object.values(PiiAttributes).includes(fieldSchema.key as PiiAttributes),
    )
    .map(({ key }) => key);

  return (
    <Card>
      <Card.Body>
        <Card.Title role='heading' aria-level={1}>
          Collected
        </Card.Title>
        {photosHeaderText && (
          <AttributeGrid colCount={photoCols}>
            <DescriptionContainer>{photosHeaderText}</DescriptionContainer>
            {govIdDocument}
            {govIdDisplayPhotos &&
              govIdDisplayPhotos.map((govIdDisplayPhoto) => (
                <PhotoWithSticker
                  key={govIdDisplayPhoto['url']}
                  imageUrl={govIdDisplayPhoto['url']}
                  isRedacted={isRedacted}
                  modalLabel={VERIFICATION_PAGE_DISPLAY[govIdDisplayPhoto['page']]}
                  stickerLabel='Display photo'
                />
              ))}
            {hasSelfiePhotoToShow && (
              <PhotoWithSticker
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                imageUrl={inquirySelfiePhotoUrl ?? fallbackSelfiePhotoUrl}
                isRedacted={isRedacted}
                modalLabel='Selfie'
                stickerLabel='Selfie'
                sourceToken={inquiry.id}
                isSelfie
              />
            )}
          </AttributeGrid>
        )}
        <DescriptionContainer>
          Attributes
          {environmentType === ApiModel.EnvironmentSandbox && (
            <SimulatedDataSticker dataSectionLabel='inquiry-attributes' />
          )}
        </DescriptionContainer>
        <AttributeGrid>
          <Attribute
            label='Name'
            value={nameFullLocalized ?? nameFull}
            redacted={isRedacted}
            placeholder='No name collected'
            dataTedata-testst='inquiry-collected__fullname'
          />
          <Attribute
            label='Government ID number'
            value={
              <Permissioned
                permission={Permission.InquiryGovIdNumberView}
                fallback={<Sticker variant='basic'>Not Available</Sticker>}
              >
                {governmentIdentificationNumbers.map((identificationNumber) => (
                  <div key={identificationNumber}>{identificationNumber}</div>
                ))}
              </Permissioned>
            }
            redacted={isRedacted}
            placeholder='No government ID number collected'
            data-test='inquiry-collected__govid'
          />
          <Attribute
            label='Birthdate'
            value={formattedBirthdate}
            redacted={isRedacted}
            placeholder='No birthdate collected'
            data-test='inquiry-collected__birthdate'
          />
          <Attribute
            label='Address'
            value={address}
            redacted={isRedacted}
            placeholder='No address collected'
            data-test='inquiry-collected__address'
          />
          {
            // Always show empty fields for Dynamic Flow if field schema exists
            (inquiryTemplateVersion != null
              ? fieldSchemaMap[PiiAttributes.EmailAddress] != null
              : emailAddress != null) && (
              <Attribute
                label='Email address'
                value={emailAddress}
                redacted={isRedacted}
                data-test='inquiry-collected__email'
                placeholder='No email address collected'
              />
            )
          }
          {
            // Always show empty fields for Dynamic Flow if field schema exists
            (inquiryTemplateVersion != null
              ? fieldSchemaMap[PiiAttributes.PhoneNumber] != null
              : phoneNumber != null) && (
              <Attribute
                label='Phone number'
                value={phoneNumber}
                redacted={isRedacted}
                data-test='inquiry-collected__phone'
                placeholder='No phone number collected'
              />
            )
          }
          {
            // Always show empty fields for Dynamic Flow if field schema exists
            (inquiryTemplateVersion != null
              ? fieldSchemaMap[PiiAttributes.SocialSecurityNumber] != null
              : socialSecurityNumber != null) && (
              <Permissioned
                permission={Permission.InquirySsnView}
                fallback={
                  <Attribute
                    label='Social security number'
                    value={truncatedSSN}
                    redacted={isRedacted}
                    toggleable={true}
                    data-test='inquiry-collected__ssn'
                    placeholder='No social security number collected'
                  />
                }
              >
                <Attribute
                  label='Social security number'
                  value={socialSecurityNumber}
                  redacted={isRedacted}
                  toggleable={true}
                  data-test='inquiry-collected__ssn'
                  placeholder='No social security number collected'
                />
              </Permissioned>
            )
          }
          {fieldNamesToShow.map((fieldName: string) => {
            // Custom fields
            // FIXME: Inquiries should not expect hardcoded field names.
            // There's work in progress to parameterize these field names
            // instead of assuming certain field names exist.
            if (fieldName === BankStatementDataField) return null;

            const fieldValue: Field = fieldsKebabCased[fieldName];
            const fieldSchema = fieldSchemaMap[fieldName];

            return (
              fieldValue &&
              fieldSchema && (
                <FieldAttributeItem
                  key={fieldSchema.key}
                  field={fieldValue}
                  fieldSchema={fieldSchema}
                  isRedacted={isRedacted}
                  wordBreak='break-all'
                  dataTest={`inquiry-collected-fields__${fieldSchema.key}`}
                />
              )
            );
          })}
        </AttributeGrid>
        {genericDocuments?.length > 0 && (
          <InquiryDocuments
            isRedacted={isRedacted}
            template={template}
            documents={genericDocuments}
            featureFlags={featureFlags}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default InquiryCollected;
